// Ordner-Wechsel-Protokoll im Control Center (design_handoff_control_center §4, Screens 02/05/07/09).
// Fachlogik unverändert: Protokoll (50) laden, Namen/Objekte auflösen, Ausführen / Erneut versuchen / Rückgängig
// mit denselben Bedingungen (lib/controlCenter/logik.ts → wechselAktion), laufend-Set und Zeilenfehler.
import React, { useEffect, useState, useCallback } from 'react';
import {
  ArrowCounterClockwise, ArrowRight, ArrowsClockwise, CheckCircle, CircleNotch, Clock, FolderSimpleDashed, Key, Prohibit,
  SkipForwardCircle, TreeStructure, User, Warning, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import {
  ladeWechselProtokoll, fuehreWechselAus, macheWechselRueckgaengig, planeWechselErneut, mieterAnzeigeName, heuteIso,
  type WechselEintrag,
} from '../../lib/wechsel/api';
import { filtereWechsel, wechselAktion, type FilterWert, type WechselAktion } from '../../lib/controlCenter/logik';
import { Segment } from '../../components/ui/Segment';
import { useViewport } from '../../components/island/hooks';
import { useMobilModus } from '../../lib/immobilien/mobil';
import { CcWidget, KopfIcon } from './teile/Rahmen';

interface MieterName { id: string; vorname: string | null; nachname: string | null; firma_name: string | null }
interface ObjektName { id: string; objektnummer: string | null; bezeichnung: string | null }

const fehlerText = (e: unknown, rueckfall: string): string =>
  (e instanceof Error ? e.message : (e as { message?: string } | null)?.message) || rueckfall;

const TYP_LABEL: Record<string, string> = {
  mieter: 'Mieter',
  eigentuemer_objekt: 'Eigentümer (Objekt)',
  eigentuemer_einheit: 'Eigentümer (WE)',
};

const FILTER_OPTIONEN: { wert: FilterWert; label: string }[] = [
  { wert: 'alle', label: 'Alle' },
  { wert: 'geplant', label: 'Geplant' },
  { wert: 'fehler', label: 'Fehler' },
  { wert: 'erledigt', label: 'Erledigt' },
];

const STATUS: Record<WechselEintrag['status'], { label: string; fg: string; bg: string; icon: Icon }> = {
  geplant: { label: 'Geplant', fg: 'var(--ink2)', bg: 'var(--fill2)', icon: Clock },
  erledigt: { label: 'Erledigt', fg: 'var(--ok)', bg: 'var(--ok-soft)', icon: CheckCircle },
  uebersprungen: { label: 'Übersprungen', fg: 'var(--warn)', bg: 'var(--warn-soft)', icon: SkipForwardCircle },
  fehler: { label: 'Fehler', fg: 'var(--bad)', bg: 'var(--bad-soft)', icon: WarningCircle },
  storniert: { label: 'Storniert', fg: 'var(--ink3)', bg: 'var(--fill2)', icon: Prohibit },
};

const KNOPF: Record<WechselAktion, { label: string; icon: Icon; tip: string; stil: React.CSSProperties }> = {
  ausfuehren: { label: 'Ausführen', icon: ArrowsClockwise, tip: 'Ausführen – Ordner jetzt verschieben', stil: { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' } },
  erneut: { label: 'Erneut versuchen', icon: ArrowsClockwise, tip: 'Erneut versuchen – Wechsel noch einmal ausführen (max. 3 Versuche)', stil: { background: 'var(--bad-soft)', color: 'var(--bad)' } },
  erneut_uebersprungen: { label: 'Erneut versuchen', icon: ArrowsClockwise, tip: 'Erneut versuchen – Wechsel neu planen und sofort ausführen', stil: { background: 'var(--bad-soft)', color: 'var(--bad)' } },
  rueckgaengig: { label: 'Rückgängig', icon: ArrowCounterClockwise, tip: 'Rückgängig – Ordner an den ursprünglichen Ort zurückverschieben', stil: { background: 'var(--fill2)', color: 'var(--ink)' } },
};

const OrdnerWechselListe: React.FC<{ spalte: string }> = ({ spalte }) => {
  const [eintraege, setEintraege] = useState<WechselEintrag[]>([]);
  const [mieterMap, setMieterMap] = useState<Record<string, MieterName>>({});
  const [eigentuemerMap, setEigentuemerMap] = useState<Record<string, MieterName>>({});
  const [objektMap, setObjektMap] = useState<Record<string, ObjektName>>({});
  const [wirdGeladen, setWirdGeladen] = useState(true);
  const [filter, setFilter] = useState<FilterWert>('alle');
  const [laufende, setLaufende] = useState<Set<string>>(new Set());
  const [zeilenFehler, setZeilenFehler] = useState<Record<string, string>>({});
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const breit = useViewport().width >= 1180;
  // Handy: Tippflächen 44 px
  const { mobil } = useMobilModus();

  const laden = useCallback(async () => {
    setWirdGeladen(true);
    try {
      const protokoll = await ladeWechselProtokoll(50);
      setEintraege(protokoll);

      const mieterAltRefIds = Array.from(new Set(protokoll.filter((e) => e.typ === 'mieter').map((e) => e.alt_ref_id).filter(Boolean)));
      const eigentuemerAltRefIds = Array.from(new Set(protokoll.filter((e) => e.typ !== 'mieter').map((e) => e.alt_ref_id).filter(Boolean)));
      const objektIds = Array.from(new Set(protokoll.map((e) => e.objekt_id).filter(Boolean)));

      const [mieterRes, eigentuemerRes, objektRes] = await Promise.all([
        mieterAltRefIds.length > 0
          ? supabase.from('mieter').select('id, vorname, nachname, firma_name').in('id', mieterAltRefIds)
          : Promise.resolve({ data: [] as MieterName[] }),
        eigentuemerAltRefIds.length > 0
          ? supabase.from('eigentuemer').select('id, vorname, nachname, firma_name').in('id', eigentuemerAltRefIds)
          : Promise.resolve({ data: [] as MieterName[] }),
        objektIds.length > 0
          ? supabase.from('objekte').select('id, objektnummer, bezeichnung').in('id', objektIds)
          : Promise.resolve({ data: [] as ObjektName[] }),
      ]);

      const mMap: Record<string, MieterName> = {};
      ((mieterRes.data || []) as MieterName[]).forEach((m) => { mMap[m.id] = m; });
      setMieterMap(mMap);

      const egMap: Record<string, MieterName> = {};
      ((eigentuemerRes.data || []) as MieterName[]).forEach((eg) => { egMap[eg.id] = eg; });
      setEigentuemerMap(egMap);

      const oMap: Record<string, ObjektName> = {};
      ((objektRes.data || []) as ObjektName[]).forEach((o) => { oMap[o.id] = o; });
      setObjektMap(oMap);
      setLadeFehler(null);
    } catch (e: unknown) {
      setLadeFehler(fehlerText(e, 'Fehler beim Laden der Ordner-Wechsel.'));
    } finally {
      setWirdGeladen(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  const gefiltert = filtereWechsel(eintraege, filter);

  const zeigeZeilenFehler = (id: string, msg: string) => {
    setZeilenFehler((prev) => ({ ...prev, [id]: msg }));
  };

  const ausfuehren = async (id: string) => {
    if (laufende.has(id)) return;
    const eintrag = eintraege.find((x) => x.id === id);
    if (!eintrag || (eintrag.status !== 'geplant' && eintrag.status !== 'fehler')) return;
    setLaufende((prev) => new Set(prev).add(id));
    setZeilenFehler((prev) => { const n = { ...prev }; delete n[id]; return n; });
    try {
      await fuehreWechselAus([id]);
      await laden();
    } catch (e: unknown) {
      zeigeZeilenFehler(id, fehlerText(e, 'Fehler beim Ausführen'));
    } finally {
      setLaufende((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const erneutVersuchen = async (id: string) => {
    if (laufende.has(id)) return;
    const eintrag = eintraege.find((x) => x.id === id);
    if (!eintrag || eintrag.status !== 'uebersprungen') return;
    setLaufende((prev) => new Set(prev).add(id));
    setZeilenFehler((prev) => { const n = { ...prev }; delete n[id]; return n; });
    try {
      await planeWechselErneut(id);
      await fuehreWechselAus([id]);
      await laden();
    } catch (e: unknown) {
      zeigeZeilenFehler(id, fehlerText(e, 'Fehler beim erneuten Versuch'));
    } finally {
      setLaufende((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const rueckgaengig = async (id: string) => {
    if (laufende.has(id)) return;
    const eintrag = eintraege.find((x) => x.id === id);
    if (!eintrag || eintrag.status !== 'erledigt') return;
    setLaufende((prev) => new Set(prev).add(id));
    setZeilenFehler((prev) => { const n = { ...prev }; delete n[id]; return n; });
    try {
      await macheWechselRueckgaengig(id);
      await laden();
    } catch (e: unknown) {
      zeigeZeilenFehler(id, fehlerText(e, 'Fehler beim Rückgängigmachen'));
    } finally {
      setLaufende((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const aktionAusloesen = (a: WechselAktion, id: string) => {
    if (a === 'ausfuehren' || a === 'erneut') void ausfuehren(id);
    else if (a === 'erneut_uebersprungen') void erneutVersuchen(id);
    else void rueckgaengig(id);
  };

  const objektLabel = (objektId: string): string => {
    const o = objektMap[objektId];
    if (!o) return '—';
    return [o.objektnummer, o.bezeichnung].filter(Boolean).join(' – ') || '—';
  };

  const altRefLabel = (eintrag: WechselEintrag): string => {
    const map = eintrag.typ === 'mieter' ? mieterMap : eigentuemerMap;
    const m = map[eintrag.alt_ref_id];
    return m ? mieterAnzeigeName(m) : 'Unbekannt';
  };

  const heute = heuteIso();
  const pfadChip: React.CSSProperties = { padding: '3px 8px', borderRadius: 8, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <CcWidget titel="Ordner-Wechsel" untertitel="Mieterwechsel-Automatik Protokoll" spalte={spalte} style={{ gap: 14 }}
      icon={<KopfIcon flaeche="linear-gradient(160deg, color-mix(in srgb, var(--info) 65%, #fff), var(--info))" farbe="#fff"><TreeStructure size={19} weight="fill" /></KopfIcon>}
      rechts={
        <div className="overflow-x-auto" style={{ maxWidth: '100%', scrollbarWidth: 'none' }}>
          <Segment<FilterWert> ariaLabel="Filter" wert={filter} onChange={setFilter} hoehe={mobil ? 44 : 32}
            eintraege={FILTER_OPTIONEN.map((o) => ({
              key: o.wert, label: o.label,
              tip: `${o.label} – Protokoll ${o.wert === 'alle' ? 'ungefiltert anzeigen' : `auf „${o.label}" filtern`}`,
            }))} />
        </div>
      }>
      {ladeFehler && (
        <div role="alert" className="flex items-center" style={{ gap: 10, padding: '11px 14px', borderRadius: 16, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle size={18} weight="fill" className="shrink-0" />{ladeFehler}
        </div>
      )}

      {wirdGeladen ? (
        <div className="flex justify-center" style={{ padding: '48px 0', color: 'var(--ink3)' }}>
          <CircleNotch size={30} weight="bold" className="animate-spin" />
        </div>
      ) : gefiltert.length === 0 ? (
        <div className="flex flex-col items-center text-center" style={{ gap: 10, padding: '40px 10px' }}>
          <span className="flex items-center justify-center" style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--fill2)', color: 'var(--ink3)' }}>
            <FolderSimpleDashed size={24} weight="bold" />
          </span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>
            {eintraege.length > 0 ? 'Keine Einträge für diesen Filter.' : 'Noch keine Ordner-Wechsel.'}
          </span>
        </div>
      ) : (
        <div className="flex flex-col overflow-auto" style={{ borderRadius: 20, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', maxHeight: breit ? 560 : undefined }}>
          {gefiltert.map((e, i) => {
            const aktion = wechselAktion(e, heute);
            const laeuft = laufende.has(e.id);
            const st = STATUS[e.status];
            const StIcon = st.icon;
            const storniert = e.status === 'storniert';
            const mieter = e.typ === 'mieter';
            const knopf = aktion ? KNOPF[aktion] : null;
            const KnopfIcon = laeuft ? CircleNotch : knopf?.icon;
            return (
              <div key={e.id} className="flex flex-wrap items-center" style={{ gap: 12, padding: '13px 14px', borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <div className="flex min-w-0 items-center" style={{ flex: '1 1 260px', gap: 12 }}>
                <span className="flex shrink-0 items-center justify-center"
                  style={{ width: 38, height: 38, borderRadius: '50%', background: mieter ? 'var(--info-soft)' : 'var(--acc-soft)', color: mieter ? 'var(--info)' : 'var(--acc)' }}>
                  {mieter ? <User size={18} weight="fill" /> : <Key size={18} weight="fill" />}
                </span>
                <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                  <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 14.5, fontWeight: 800, textDecoration: storniert ? 'line-through' : 'none' }}>{altRefLabel(e)}</span>
                    {TYP_LABEL[e.typ] && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{TYP_LABEL[e.typ]}</span>}
                    <span className="inline-flex items-center whitespace-nowrap"
                      style={{ height: 22, padding: '0 9px', gap: 5, borderRadius: 11, fontSize: 11.5, fontWeight: 800, background: st.bg, color: st.fg, textDecoration: storniert ? 'line-through' : 'none' }}>
                      <StIcon size={12} weight="fill" />{st.label}
                    </span>
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
                    {objektLabel(e.objekt_id)} · fällig am {new Date(e.faellig_am).toLocaleDateString('de-DE')}
                  </span>
                  {(e.status === 'fehler' || e.status === 'uebersprungen') && e.fehler && (
                    <span className="flex items-start" style={{ gap: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--bad)' }}>
                      <Warning size={13} weight="bold" className="shrink-0" style={{ marginTop: 2 }} />{e.fehler}
                    </span>
                  )}
                  {e.status === 'erledigt' && (e.von_pfad || e.nach_pfad) && (
                    <div className="flex flex-wrap items-center" style={{ gap: 6, marginTop: 2, fontFamily: "'SF Mono', ui-monospace, Menlo, monospace", fontSize: 11.5, color: 'var(--ink2)' }}>
                      <span title={e.von_pfad || undefined} style={{ ...pfadChip, background: 'var(--fill2)' }}>{e.von_pfad || '—'}</span>
                      <ArrowRight size={12} weight="bold" color="var(--ink3)" aria-label="nach" />
                      <span title={e.nach_pfad || undefined} style={{ ...pfadChip, background: 'var(--ok-soft)', color: 'var(--ok)' }}>{e.nach_pfad || '—'}</span>
                    </div>
                  )}
                  {zeilenFehler[e.id] && <span role="alert" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bad)' }}>{zeilenFehler[e.id]}</span>}
                </div>
                </div>
                {knopf && aktion && KnopfIcon && (
                  <button type="button" onClick={() => aktionAusloesen(aktion, e.id)} disabled={laeuft} data-tip={knopf.tip}
                    className="inline-flex shrink-0 items-center whitespace-nowrap"
                    style={{ height: mobil ? 44 : 36, padding: '0 15px', gap: 7, borderRadius: mobil ? 22 : 18, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
                      cursor: laeuft ? 'wait' : 'pointer', opacity: laeuft ? 0.5 : 1, ...knopf.stil }}>
                    <KnopfIcon size={14} weight="bold" className={laeuft ? 'animate-spin' : undefined} />{knopf.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CcWidget>
  );
};

export default OrdnerWechselListe;
